import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { getTierFromProductId, SubscriptionTier } from '@/lib/subscription';
import { toast } from 'sonner';

interface SubscriptionState {
  subscribed: boolean;
  productId: string | null;
  tier: SubscriptionTier;
  subscriptionEnd: string | null;
  isLoading: boolean;
  // Additional fields for subscription_started analytics event
  subscriptionId: string | null;
  currency: string | null;
  billingCycle: 'monthly' | 'annual' | null;
  amount: number | null;
  // Additional fields for subscription_canceled analytics event
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  subscriptionStartedAt: string | null;
  cancellationReason: string | null;
}

export const useSubscription = () => {
  const { session } = useAuth();
  const [state, setState] = useState<SubscriptionState>({
    subscribed: false,
    productId: null,
    tier: 'free',
    subscriptionEnd: null,
    isLoading: true,
    subscriptionId: null,
    currency: null,
    billingCycle: null,
    amount: null,
    cancelAtPeriodEnd: false,
    canceledAt: null,
    subscriptionStartedAt: null,
    cancellationReason: null,
  });

  const checkSubscription = useCallback(async () => {
    if (!session?.access_token) {
      setState(prev => ({ ...prev, isLoading: false }));
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        // For auth errors (401), silently fail and let the auto-refresh retry
        // This handles the race condition when tokens are being refreshed
        if (error.message?.includes('401') || error.message?.includes('non-2xx')) {
          console.log('Subscription check: token may be refreshing, will retry');
        } else {
          console.error('Error checking subscription:', error);
        }
        setState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      const tier = getTierFromProductId(data.productId);
      setState({
        subscribed: data.subscribed,
        productId: data.productId,
        tier,
        subscriptionEnd: data.subscriptionEnd,
        isLoading: false,
        subscriptionId: data.subscriptionId || null,
        currency: data.currency || null,
        billingCycle: data.billingCycle || null,
        amount: data.amount ?? null,
        cancelAtPeriodEnd: data.cancelAtPeriodEnd || false,
        canceledAt: data.canceledAt || null,
        subscriptionStartedAt: data.subscriptionStartedAt || null,
        cancellationReason: data.cancellationReason || null,
      });
    } catch (error) {
      console.error('Error checking subscription:', error);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [session?.access_token]);

  // Check subscription on mount and auth changes
  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(checkSubscription, 60000);
    return () => clearInterval(interval);
  }, [checkSubscription]);

  const openCheckout = async (lookupKey: string) => {
    if (!session?.access_token) {
      toast.error('Please sign in to subscribe');
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { lookupKey },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        toast.error('Failed to create checkout session');
        console.error('Checkout error:', error);
        return;
      }

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      toast.error('Failed to create checkout session');
      console.error('Checkout error:', error);
    }
  };

  const openCustomerPortal = async () => {
    if (!session?.access_token) {
      toast.error('Please sign in to manage your subscription');
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        toast.error('Failed to open billing portal');
        console.error('Portal error:', error);
        return;
      }

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      toast.error('Failed to open billing portal');
      console.error('Portal error:', error);
    }
  };

  return {
    ...state,
    checkSubscription,
    openCheckout,
    openCustomerPortal,
  };
};
